import React, { useCallback, useEffect, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function TabPreview({ project }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [buying, setBuying] = useState("");

  // Load preview (auto-refreshes on project.updated_at change → live-refresh on Layout save)
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/ph360/preview"); setHtml(data.html); }
      catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, [project.updated_at]);

  const loadBilling = useCallback(async () => {
    try {
      const [pk, ent] = await Promise.all([
        api.get("/ph360/billing/packages"),
        api.get("/ph360/billing/entitlements"),
      ]);
      setPackages(pk.data);
      setEntitlements(ent.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { loadBilling(); }, [loadBilling]);

  // Handle return from Stripe (session_id in URL)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("checkout_session_id");
    const cancelled = params.get("checkout") === "cancelled";
    if (cancelled) {
      toast.info("Checkout cancelled");
      // Clear from URL
      params.delete("checkout"); window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
      return;
    }
    if (!sid) return;
    let attempts = 0;
    const maxAttempts = 6;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/ph360/billing/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success(data.kind === "one_time" ? "Book unlocked — you can now export." : "Publisher plan active — unlimited exports.");
          await loadBilling();
          params.delete("checkout_session_id"); window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout session expired.");
          return;
        }
        if (attempts < maxAttempts) setTimeout(poll, 2000);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    };
    poll();
  }, [loadBilling]);

  const buy = async (package_id) => {
    setBuying(package_id);
    try {
      const { data } = await api.post("/ph360/billing/checkout", {
        package_id, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err)); setBuying(""); }
  };

  const download = async (kind) => {
    try {
      // Cookie-based auth — `credentials: "include"` sends the httpOnly session cookie.
      const res = await fetch(`${API}/ph360/export/${kind}`, {
        credentials: "include",
      });
      if (!res.ok) {
        let msg = `Export failed (${res.status})`;
        try { const j = await res.json(); if (j.detail) msg = j.detail; }
        catch (parseErr) { console.debug("Non-JSON error body:", parseErr); }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project.book_info?.title || "ebook"}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) { toast.error(e.message || "Export failed"); }
  };

  if (!project.structured?.chapters?.length && !project.raw_text) {
    return <p>No project yet. Import content first.</p>;
  }

  const hasAccess = entitlements?.is_admin ||
    entitlements?.is_free_user ||
    entitlements?.has_subscription ||
    (entitlements?.unlocked_projects || []).length > 0; // this user has unlocked at least one project

  return (
    <>
      <h2>Preview / Export</h2>
      {project.use_final_html
        ? <div className="notice notice-success"><p><strong>Export Mode:</strong> Using saved Final Editor manuscript.</p></div>
        : <div className="notice notice-info"><p><strong>Export Mode:</strong> Using auto-formatted manuscript. Use the Final Editor tab for last-mile polish.</p></div>}

      {/* Billing pane */}
      <BillingPane
        entitlements={entitlements}
        packages={packages}
        onBuy={buy}
        buying={buying}
      />

      <div className="ef360-actions">
        <button className="button button-primary" onClick={()=>download("pdf")} data-testid="preview-download-pdf" disabled={!hasAccess}>
          {hasAccess ? "Download PDF" : "🔒 Download PDF (locked)"}
        </button>
        <button className="button" onClick={()=>download("docx")} data-testid="preview-download-docx" disabled={!hasAccess}>
          {hasAccess ? "Download DOCX" : "🔒 Download DOCX (locked)"}
        </button>
      </div>

      <SharePane projectId={project.id} />
      <ReferralPane onCreditsRedeemed={loadBilling} />

      {loading ? <p>Rendering preview…</p> : (
        <iframe title="Preview" className="ef360-preview-frame" srcDoc={html} data-testid="preview-frame"/>
      )}
    </>
  );
}

function BillingPane({ entitlements, packages, onBuy, buying }) {
  const [team, setTeam] = React.useState(null);
  const [inviteEmail, setInviteEmail] = React.useState("");
  const [inviting, setInviting] = React.useState(false);
  const [showTeamPlans, setShowTeamPlans] = React.useState(false);

  const loadTeam = React.useCallback(async () => {
    try { const { data } = await api.get("/ph360/team"); setTeam(data.team); }
    catch (err) {
      // 404 (no team yet) is the normal case for solo users — don't spam the console
      if (err?.response?.status !== 404) console.debug("loadTeam failed:", err?.message);
    }
  }, []);
  React.useEffect(() => { loadTeam(); }, [loadTeam]);

  if (!packages || !entitlements) return null;
  const hasSub = entitlements.has_subscription;
  const unlockedCount = (entitlements.unlocked_projects || []).length;
  const freeAccessReason = entitlements.is_admin
    ? "admin"
    : entitlements.is_free_user
      ? "free-list"
      : null;

  const createTeam = async () => {
    try { await api.post("/ph360/team/create", { name: "My Publishing Team" }); await loadTeam(); toast.success("Team created"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const invite = async (e) => {
    e?.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data } = await api.post("/ph360/team/invite", { email: inviteEmail.trim() });
      toast.success(data.status === "added_directly" ? "Member added" : "Invite sent");
      setInviteEmail(""); await loadTeam();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setInviting(false); }
  };
  const removeMember = async (uid) => {
    if (!window.confirm("Remove this member?")) return;
    try { await api.delete(`/ph360/team/members/${uid}`); await loadTeam(); toast.success("Removed"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const teamSection = team && team.active ? (
    <div className="notice notice-success" data-testid="team-panel">
      <p><strong>Team Publisher active</strong> · {team.plan} · {team.seats_used}/{team.seat_limit} seats used</p>
      {team.is_owner && (
        <>
          <div style={{margin:"8px 0"}}>
            <strong>Members</strong>
            <ul style={{margin:"4px 0 0 18px"}}>
              <li>{team.owner.email} (owner)</li>
              {team.members.map(m => (
                <li key={m.user_id} data-testid={`team-member-${m.user_id}`}>
                  {m.email} <button className="button" style={{padding:"1px 6px",fontSize:11}} onClick={()=>removeMember(m.user_id)} data-testid={`team-remove-${m.user_id}`}>Remove</button>
                </li>
              ))}
              {(team.invited_emails || []).map(em => <li key={em}><em>{em}</em> (invited)</li>)}
            </ul>
          </div>
          <form onSubmit={invite} style={{display:"flex", gap:6, alignItems:"center", marginTop:6}}>
            <input type="email" placeholder="teammate@example.com" value={inviteEmail} onChange={(e)=>setInviteEmail(e.target.value)} style={{flex:"0 0 260px"}} data-testid="team-invite-email"/>
            <button type="submit" className="button button-primary" disabled={inviting} data-testid="team-invite-submit">
              {inviting ? "Inviting…" : "Invite"}
            </button>
          </form>
        </>
      )}
    </div>
  ) : null;

  if (freeAccessReason) {
    const label = freeAccessReason === "admin"
      ? "Unlimited exports enabled."
      : `Free access granted${entitlements.free_user_note ? ` (${entitlements.free_user_note})` : ""} — unlimited exports.`;
    return (
      <>
        <div className="notice notice-success" data-testid="billing-free-access">
          <p><strong>{freeAccessReason === "admin" ? "🛠 Admin" : "✨ Free access"}</strong> — {label}</p>
        </div>
        {teamSection}
      </>
    );
  }

  if (hasSub) {
    const sub = entitlements.subscription || {};
    return (
      <>
        <div className="notice notice-success" data-testid="billing-subscription-active">
          <p><strong>Publisher plan active</strong> ({sub.plan}) — unlimited PDF/DOCX exports. {unlockedCount > 0 ? `Also unlocked ${unlockedCount} book(s).` : ""}</p>
          {team ? null : (
            <p style={{marginTop:6}}>
              <button className="button" onClick={createTeam} data-testid="team-create">Create Publishing Team (requires Team subscription)</button>
            </p>
          )}
        </div>
        {teamSection}
      </>
    );
  }
  return (
    <>
      {teamSection}
      <div className="notice notice-warning" data-testid="billing-pane">
        <p><strong>Exports are locked.</strong> Unlock this book once, subscribe as an individual Publisher, or invite a team.</p>
        <div style={{display:"flex", gap:10, marginTop:10, flexWrap:"wrap"}}>
          <button className="button button-primary" onClick={()=>onBuy("book_unlock")} disabled={!!buying} data-testid="buy-book-unlock">
            {buying === "book_unlock" ? "Redirecting…" : "Unlock this book ($5.99)"}
          </button>
          <button className="button" onClick={()=>onBuy("publisher_monthly")} disabled={!!buying} data-testid="buy-publisher-monthly">
            {buying === "publisher_monthly" ? "Redirecting…" : "Publisher — $19/mo"}
          </button>
          <button className="button" onClick={()=>onBuy("publisher_yearly")} disabled={!!buying} data-testid="buy-publisher-yearly">
            {buying === "publisher_yearly" ? "Redirecting…" : "Publisher — $160/yr (save 30%)"}
          </button>
          <button className="button" onClick={()=>setShowTeamPlans(!showTeamPlans)} data-testid="show-team-plans">
            {showTeamPlans ? "Hide team plans" : "Team plans →"}
          </button>
        </div>
        {showTeamPlans && (
          <div style={{marginTop:10, display:"flex", gap:10, flexWrap:"wrap"}} data-testid="team-plan-buttons">
            <button className="button" onClick={()=>onBuy("team_3_monthly")} disabled={!!buying} data-testid="buy-team-3-monthly">Team 3 seats — $49/mo</button>
            <button className="button" onClick={()=>onBuy("team_3_yearly")} disabled={!!buying} data-testid="buy-team-3-yearly">Team 3 seats — $490/yr</button>
            <button className="button" onClick={()=>onBuy("team_10_monthly")} disabled={!!buying} data-testid="buy-team-10-monthly">Team 10 seats — $99/mo</button>
            <button className="button" onClick={()=>onBuy("team_10_yearly")} disabled={!!buying} data-testid="buy-team-10-yearly">Team 10 seats — $990/yr</button>
          </div>
        )}
        <p className="description" style={{marginTop:8}}>
          Powered by Stripe. Test mode: use card 4242 4242 4242 4242.
        </p>
      </div>
    </>
  );
}


function SharePane({ projectId }) {
  const [state, setState] = React.useState(null); // {enabled, token, views, created_at}
  const [analytics, setAnalytics] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [showAnalytics, setShowAnalytics] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/ph360/projects/${projectId}/share`);
      setState(data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [projectId]);
  React.useEffect(() => { load(); }, [load]);

  const loadAnalytics = React.useCallback(async () => {
    if (!projectId || !state?.enabled) return;
    try {
      const { data } = await api.get(`/ph360/projects/${projectId}/share/analytics`);
      setAnalytics(data);
    } catch (err) {
      console.debug("share analytics load failed:", err?.message);
    }
  }, [projectId, state?.enabled]);
  React.useEffect(() => { if (showAnalytics) loadAnalytics(); }, [showAnalytics, loadAnalytics]);

  if (!projectId) return null;

  const shareUrl = state?.token ? `${window.location.origin}/share/${state.token}` : "";

  const mint = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ph360/projects/${projectId}/share`);
      setState(data);
      toast.success("Share link ready");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("Revoke the current share link? Anyone with the URL will lose access.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/ph360/projects/${projectId}/share`);
      setState(data); setAnalytics(null); setShowAnalytics(false);
      toast.success("Share link revoked");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch (err) {
      console.debug("Clipboard write failed:", err?.message);
      toast.error("Copy failed — select and copy manually");
    }
  };
  const openPreview = () => { if (shareUrl) window.open(shareUrl, "_blank", "noopener"); };

  return (
    <div className="notice notice-info" data-testid="share-pane" style={{marginTop:14}}>
      <p style={{margin:"0 0 6px"}}><strong>Public share link</strong> — read-only preview, no download.</p>
      {state?.enabled ? (
        <>
          <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
            <input readOnly value={shareUrl} data-testid="share-url"
              style={{flex:"1 1 320px", minWidth:260, fontFamily:"ui-monospace, SFMono-Regular, monospace"}}
              onFocus={(e)=>e.target.select()} />
            <button className="button" onClick={copy} data-testid="share-copy">Copy</button>
            <button className="button" onClick={openPreview} data-testid="share-open">Open preview</button>
            <button className="button" onClick={()=>setShowAnalytics(!showAnalytics)} data-testid="share-analytics-toggle">
              {showAnalytics ? "Hide analytics" : "Analytics"}
            </button>
            <button className="button" onClick={revoke} disabled={busy} data-testid="share-revoke">Revoke</button>
          </div>
          <p className="description" style={{marginTop:6}} data-testid="share-views">
            Views: {state.views ?? 0}. Changes you save automatically appear in the shared preview.
          </p>
          {showAnalytics && (
            <div data-testid="share-analytics" style={{marginTop:10, padding:12, background:"#fff", border:"1px solid #dcdcde"}}>
              <p style={{margin:"0 0 8px", fontWeight:600}}>
                Last 30 days · Total views: {analytics?.total_views ?? "—"}
                {analytics?.last_viewed_at && (
                  <span className="description" style={{marginLeft:10, fontWeight:400}}>
                    Last viewed: {new Date(analytics.last_viewed_at).toLocaleString()}
                  </span>
                )}
              </p>
              {analytics?.series ? (
                <div style={{width:"100%", height:220}} data-testid="share-analytics-chart">
                  <ResponsiveContainer>
                    <LineChart data={analytics.series} margin={{top:6, right:12, left:-20, bottom:6}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ececec" />
                      <XAxis dataKey="day" tickFormatter={(d)=>d.slice(5)} fontSize={11} stroke="#8c8f94" />
                      <YAxis allowDecimals={false} fontSize={11} stroke="#8c8f94" />
                      <Tooltip contentStyle={{fontSize:12}} />
                      <Line type="monotone" dataKey="views" stroke="#2271b1" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="description">Loading chart…</p>}
            </div>
          )}
        </>
      ) : (
        <div style={{display:"flex", gap:10, alignItems:"center", flexWrap:"wrap"}}>
          <button className="button button-primary" onClick={mint} disabled={busy} data-testid="share-create">
            {busy ? "Creating…" : "Create share link"}
          </button>
          <span className="description">Nothing shared yet.</span>
        </div>
      )}
    </div>
  );
}

function ReferralPane({ onCreditsRedeemed }) {
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [showLedger, setShowLedger] = React.useState(false);

  const load = React.useCallback(async () => {
    try { const { data } = await api.get("/ph360/referrals/me"); setData(data); }
    catch (err) { toast.error(formatApiError(err)); }
  }, []);
  React.useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const shareUrl = `${window.location.origin}/register?ref=${data.referral_code}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Referral link copied"); }
    catch (err) {
      console.debug("Referral copy failed:", err?.message);
      toast.error("Copy failed");
    }
  };
  const canRedeem = (data.credits_balance || 0) >= (data.unlock_price || 5.99);
  const redeem = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/ph360/referrals/redeem-unlock", {});
      toast.success(`Book unlocked. New credit balance: $${r.new_balance.toFixed(2)}`);
      await load();
      onCreditsRedeemed?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="notice notice-info" data-testid="referral-pane" style={{marginTop:14}}>
      <p style={{margin:"0 0 6px"}}><strong>Referral program</strong> — earn {data.percent_reward}% of a friend's first Publisher payment as credits (redeemable for book unlocks).</p>
      <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
        <span style={{fontFamily:"ui-monospace, SFMono-Regular, monospace"}} data-testid="referral-code">
          Your code: <strong>{data.referral_code}</strong>
        </span>
        <input readOnly value={shareUrl} data-testid="referral-url"
          style={{flex:"1 1 260px", minWidth:220, fontFamily:"ui-monospace, SFMono-Regular, monospace"}}
          onFocus={(e)=>e.target.select()} />
        <button className="button" onClick={copy} data-testid="referral-copy">Copy link</button>
      </div>
      <div style={{marginTop:10, display:"flex", gap:16, alignItems:"center", flexWrap:"wrap"}}>
        <span data-testid="referral-count">Signups referred: <strong>{data.referred_count}</strong></span>
        <span data-testid="referral-balance">Credits available: <strong>${(data.credits_balance || 0).toFixed(2)}</strong></span>
        <span className="description">Lifetime earned: ${(data.credits_earned_total || 0).toFixed(2)}</span>
        <button className="button" onClick={()=>setShowLedger(!showLedger)} data-testid="referral-history-toggle">
          {showLedger ? "Hide history" : "History"}
        </button>
        <button className="button button-primary" onClick={redeem} disabled={busy || !canRedeem} data-testid="referral-redeem">
          {busy ? "Redeeming…" : `Redeem for book unlock ($${(data.unlock_price || 5.99).toFixed(2)})`}
        </button>
      </div>
      {showLedger && (
        <div style={{marginTop:10}} data-testid="referral-history">
          {data.recent.length === 0 ? (
            <p className="description">No referrals yet. Share your link to start earning.</p>
          ) : (
            <table className="wp-list-table widefat striped">
              <thead><tr><th>When</th><th>Referred email</th><th>Plan</th><th>Credit</th></tr></thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr key={`${r.created_at || ""}-${r.referred_email || "anon"}-${r.package_id || ""}`}>
                    <td>{(r.created_at || "").slice(0,10)}</td>
                    <td>{r.referred_email || "—"}</td>
                    <td>{r.package_id}</td>
                    <td>${(r.amount || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
